"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Card } from "@/components/ui/card";
import { Loader, Calendar as CalendarIcon } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect } from "react";
export default function ContactForm() {
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.15
  });
  const {
    ref: calendarRef,
    isVisible: calendarVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.15
  });
  return <section className="bg-light-background py-20 px-4">
      <div className="container mx-auto max-w-6xl">
        <FormProvider className="w-full" formId="contact-form-main" sectionName="contact-form-section" defaultValues={{
        name: "",
        email: "",
        phone: "",
        subjectInterested: "",
        currentYearGroup: "",
        message: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 50
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        subjectInterested: {
          required: true,
          minLength: 2,
          maxLength: 100
        },
        currentYearGroup: {
          required: true,
          minLength: 1,
          maxLength: 50
        },
        message: {
          required: true,
          minLength: 10
        }
      }} submitText="Send Message" successMessage="Thank you for reaching out! We'll get back to you within 24 hours.">
          <Card className="shadow-medium overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              {}
              <div ref={formRef} className={`p-8 transition-all duration-[var(--duration-medium)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                <ContactFormFields />
              </div>

              {}
              <div ref={calendarRef} className={`bg-muted-light-background p-8 flex flex-col items-center justify-start border-l border-border transition-all duration-[var(--duration-medium)] delay-100 ${calendarVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
                <div className="flex flex-col items-center gap-6 w-full">
                  <div className="flex items-center gap-2 text-primary">
                    <SafeHydrate><CalendarIcon className="size-8" /></SafeHydrate>
                    <h3 className="text-xl font-display font-semibold">Schedule a Call</h3>
                  </div>
                  <TidyCalEmbed />
                </div>
              </div>
            </div>
          </Card>
        </FormProvider>
      </div>
    </section>;
}
function ContactFormFields() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-6">
        <div className="p-6 bg-secondary-light border-2 border-secondary rounded-lg text-center">
          <p className="text-foreground-secondary font-semibold text-lg">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="hover-lift">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <div className="mb-8">
          <h2 className="text-3xl font-display font-bold text-primary mb-2">Contact Form</h2>
          <p className="text-muted-foreground">Fill out the form below and we'll respond within 24 hours.</p>
        </div>

        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem className="animate-fade-in-up">
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter your name" {...field} className="focus-visible:ring-primary/30 transition-all duration-[var(--duration-fast)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem className="animate-fade-in-up-delay-100">
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" {...field} className="focus-visible:ring-primary/30 transition-all duration-[var(--duration-fast)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[\d\s\-\+\(\)]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem className="animate-fade-in-up-delay-200">
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="0800 123 4567" {...field} className="focus-visible:ring-primary/30 transition-all duration-[var(--duration-fast)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subjectInterested" rules={{
        required: "Subject interested is required",
        minLength: {
          value: 2,
          message: "Subject must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem className="animate-fade-in-up-delay-200">
              <FormLabel>Subject interested</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Science, Maths, English" {...field} className="focus-visible:ring-primary/30 transition-all duration-[var(--duration-fast)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="currentYearGroup" rules={{
        required: "Current year group is required",
        minLength: {
          value: 1,
          message: "Year group is required"
        }
      }} render={({
        field
      }) => <FormItem className="animate-fade-in-up-delay-200">
              <FormLabel>Current year group</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Year 9, Year 11, KS3" {...field} className="focus-visible:ring-primary/30 transition-all duration-[var(--duration-fast)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        }
      }} render={({
        field
      }) => <FormItem className="animate-fade-in-up-delay-200">
              <FormLabel>Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your tutoring needs..." {...field} className="min-h-32 focus-visible:ring-primary/30 transition-all duration-[var(--duration-fast)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full btn-primary">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending...
            </> : 'Send Message'}
        </Button>
      </div>
    </Form>;
}
function TidyCalEmbed() {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://asset-tidycal.b-cdn.net/js/embed.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  return <div className="w-full max-w-2xl min-h-[500px]">
      <div className="tidycal-embed" data-path="educationfamilytutors"></div>
    </div>;
}